import PropTypes from "prop-types";
import { matchPath, useLocation, Link } from "react-router-dom";

// material-ui
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import { Tooltip } from "@mui/material";

// project-imports
import Dot from "components/@extended/Dot";
import IconButton from "components/@extended/IconButton";

import useConfig from "hooks/useConfig";
import { MenuOrientation, NavActionType } from "config";
import { handlerDrawerOpen, useGetMenuMaster } from "api/menu";
import { V2 } from "layout/Dashboard/Drawer/drawerV2Tokens";
import { ADMIN_DRAWER } from "../../MiniDrawerStyled";

const whiteIconFilter =
  "brightness(0) saturate(100%) invert(100%)";

// ==============================|| NAVIGATION - ITEM (Admin teal rail) ||============================== //

export default function NavItem({ item, level, isParents = false }) {
  const theme = useTheme();
  const downLG = useMediaQuery(theme.breakpoints.down("lg"));

  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const { menuOrientation } = useConfig();

  let itemTarget = "_self";
  if (item.target) {
    itemTarget = "_blank";
  }

  const Icon = item.icon;
  const itemIcon = item.icon ? (
    <Icon
      variant="Bulk"
      size={drawerOpen ? 20 : 22}
      style={{
        ...(menuOrientation === MenuOrientation.HORIZONTAL &&
          isParents && { fontSize: 20, stroke: "1.5" }),
      }}
    />
  ) : (
    false
  );

  const { pathname } = useLocation();
  const isSelected = !!matchPath(item?.link || item?.url || "", pathname);

  return (
    <>
      {menuOrientation === MenuOrientation.VERTICAL || downLG ? (
        <Box sx={{ position: "relative" }}>
          <ListItemButton
            component={Link}
            to={item.url}
            target={itemTarget}
            disabled={item.disabled}
            selected={isSelected}
            sx={{
              zIndex: 1201,
              pl: drawerOpen ? `${level * 20}px` : 1.5,
              py: !drawerOpen && level === 1 ? 1.1 : 0.85,
              fontFamily: V2.fontBody,
              color: ADMIN_DRAWER.ink,
              ...(drawerOpen &&
                level === 1 && {
                  mx: 1,
                  my: 0.2,
                  borderRadius: "12px",
                  bgcolor: isSelected ? ADMIN_DRAWER.soft : "transparent",
                  "&:hover": { bgcolor: ADMIN_DRAWER.softHover },
                  "&.Mui-selected": {
                    bgcolor: ADMIN_DRAWER.soft,
                    color: ADMIN_DRAWER.ink,
                    "&:hover": { bgcolor: ADMIN_DRAWER.softHover },
                  },
                }),
              ...(drawerOpen &&
                level !== 1 && {
                  mx: 1,
                  borderRadius: "10px",
                  "&:hover": { bgcolor: ADMIN_DRAWER.soft },
                  "&.Mui-selected": {
                    bgcolor: ADMIN_DRAWER.soft,
                    "&:hover": { bgcolor: ADMIN_DRAWER.softHover },
                  },
                }),
              ...(!drawerOpen && {
                px: 2.75,
                justifyContent: "center",
                "&:hover": { bgcolor: "transparent" },
                "&.Mui-selected": { bgcolor: "transparent" },
              }),
            }}
            {...(downLG && { onClick: () => handlerDrawerOpen(false) })}
          >
            {itemIcon && drawerOpen && (
              <ListItemIcon
                sx={{
                  minWidth: 38,
                  color: ADMIN_DRAWER.ink,
                  filter: whiteIconFilter,
                  "& svg": { color: `${ADMIN_DRAWER.ink} !important` },
                }}
              >
                {itemIcon}
              </ListItemIcon>
            )}

            {itemIcon && !drawerOpen && (
              <Tooltip title={item.title} placement="right" arrow>
                <ListItemIcon
                  sx={{
                    minWidth: 38,
                    width: 42,
                    height: 42,
                    borderRadius: "12px",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: isSelected ? ADMIN_DRAWER.soft : "transparent",
                    color: ADMIN_DRAWER.ink,
                    filter: whiteIconFilter,
                    "&:hover": { bgcolor: ADMIN_DRAWER.softHover },
                  }}
                >
                  {itemIcon}
                </ListItemIcon>
              </Tooltip>
            )}

            {!itemIcon && drawerOpen && (
              <ListItemIcon sx={{ minWidth: 30 }}>
                <Dot size={isSelected ? 6 : 5} sx={{ bgcolor: ADMIN_DRAWER.ink }} />
              </ListItemIcon>
            )}

            {(drawerOpen || (!drawerOpen && level !== 1)) && (
              <ListItemText
                primary={
                  <Typography
                    variant="h6"
                    sx={{
                      color: ADMIN_DRAWER.ink,
                      fontWeight: isSelected ? 600 : 400,
                      fontFamily: V2.fontBody,
                      fontSize: 14,
                    }}
                  >
                    {item.title}
                  </Typography>
                }
              />
            )}
            {(drawerOpen || (!drawerOpen && level !== 1)) && item.chip && (
              <Chip
                color={item.chip.color}
                variant={item.chip.variant}
                size={item.chip.size}
                label={item.chip.label}
                avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
              />
            )}
          </ListItemButton>
          {(drawerOpen || (!drawerOpen && level !== 1)) &&
            item?.actions &&
            item?.actions.map((action, index) => {
              const ActionIcon = action.icon;
              const callAction = action?.function;
              return (
                <IconButton
                  key={index}
                  {...(action.type === NavActionType.FUNCTION && {
                    onClick: (event) => {
                      event.stopPropagation();
                      callAction();
                    },
                  })}
                  {...(action.type === NavActionType.LINK && {
                    component: Link,
                    to: action.url,
                    target: action.target ? "_blank" : "_self",
                  })}
                  color="secondary"
                  variant="outlined"
                  sx={{
                    position: "absolute",
                    top: 12,
                    right: 20,
                    zIndex: 1202,
                    width: 20,
                    height: 20,
                    p: 0.25,
                    color: ADMIN_DRAWER.ink,
                    borderColor: ADMIN_DRAWER.line,
                  }}
                >
                  <ActionIcon size={12} style={{ marginLeft: 1 }} />
                </IconButton>
              );
            })}
        </Box>
      ) : (
        <ListItemButton
          component={Link}
          to={item.url}
          target={itemTarget}
          disabled={item.disabled}
          selected={isSelected}
        >
          {itemIcon && (
            <ListItemIcon sx={{ minWidth: 36, color: ADMIN_DRAWER.ink }}>
              {itemIcon}
            </ListItemIcon>
          )}
          <ListItemText
            primary={
              <Typography variant="h6" sx={{ color: ADMIN_DRAWER.ink }}>
                {item.title}
              </Typography>
            }
          />
        </ListItemButton>
      )}
    </>
  );
}

NavItem.propTypes = {
  item: PropTypes.object,
  level: PropTypes.number,
  isParents: PropTypes.bool,
};
